import { useRef, useState, useEffect } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import {
  TextField,
  Typography,
  InputAdornment,
  CircularProgress,
} from '@mui/material';

import { CallTranscription } from 'store/api/hospitals/types';

import * as Styles from './styles';

interface TranscriptViewerProps {
  isLoading?: boolean;
  currentTime?: number;
  error?: string | null;
  highlightText?: string;
  transcription: CallTranscription | null;
  onTimestampClick?: (time: number) => void;
}

export const TranscriptViewer = ({
  error = null,
  transcription,
  currentTime = 0,
  onTimestampClick,
  isLoading = false,
  highlightText = '',
}: TranscriptViewerProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeUtteranceIndex, setActiveUtteranceIndex] = useState<
    number | null
  >(null);
  const utteranceRefs = useRef<(HTMLDivElement | null)[]>([]);

  const utterances = transcription?.transcript_json?.utterances || [];

  useEffect(() => {
    if (!currentTime || utterances.length === 0) {
      return;
    }

    const currentIndex = utterances.findIndex((utterance, index) => {
      const nextUtterance = utterances[index + 1];
      return (
        utterance.startTime <= currentTime &&
        (!nextUtterance || currentTime < nextUtterance.startTime)
      );
    });

    if (currentIndex !== -1 && currentIndex !== activeUtteranceIndex) {
      setActiveUtteranceIndex(currentIndex);

      const ref = utteranceRefs.current[currentIndex];
      if (ref) {
        ref.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }
  }, [currentTime, utterances, activeUtteranceIndex]);

  const getSpeakerLabel = (speaker: string): string => {
    const labels: Record<string, string> = {
      spk_1: 'Client',
      spk_0: 'AI Agent',
      spk_2: 'Veterinarian',
    };
    return labels[speaker] || speaker;
  };

  const getSpeakerColor = (speaker: string): string => {
    const colors: Record<string, string> = {
      spk_0: '#1976d2',
      spk_1: '#2e7d32',
      spk_2: '#ed6c02',
      spk_3: '#9c27b0',
      spk_4: '#d32f2f',
      spk_5: '#00796b',
      spk_6: '#f57c00',
      spk_7: '#5e35b1',
    };
    return colors[speaker] || '#757575';
  };

  const formatTimestamp = (seconds: number | undefined): string => {
    if (seconds === undefined || seconds === null || isNaN(seconds)) {
      return '0:00';
    }
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const highlightSearchText = (text: string, query: string): JSX.Element => {
    if (!query) {
      return <>{text}</>;
    }

    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return (
      <>
        {parts.map((part, index) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <Styles.HighlightedText key={index}>{part}</Styles.HighlightedText>
          ) : (
            <span key={index}>{part}</span>
          )
        )}
      </>
    );
  };

  const filteredUtterances = searchQuery
    ? utterances.filter((utterance) =>
        utterance.text.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : utterances;

  if (isLoading) {
    return (
      <Styles.Container>
        <Styles.LoadingContainer>
          <CircularProgress />
          <Typography sx={{ mt: 2 }} variant="body2">
            Loading transcription...
          </Typography>
        </Styles.LoadingContainer>
      </Styles.Container>
    );
  }

  if (error) {
    return (
      <Styles.Container>
        <Styles.ErrorContainer>
          <Typography color="error" variant="body1">
            {error}
          </Typography>
        </Styles.ErrorContainer>
      </Styles.Container>
    );
  }

  if (!transcription) {
    return (
      <Styles.Container>
        <Styles.EmptyState>
          <Typography variant="body1" color="textSecondary">
            No transcription available
          </Typography>
        </Styles.EmptyState>
      </Styles.Container>
    );
  }

  if (transcription.transcription_status === 'queued') {
    return (
      <Styles.Container>
        <Styles.StatusContainer>
          <CircularProgress size={24} />
          <Typography sx={{ ml: 2 }} variant="body2">
            Transcription queued for processing...
          </Typography>
        </Styles.StatusContainer>
      </Styles.Container>
    );
  }

  if (transcription.transcription_status === 'processing') {
    return (
      <Styles.Container>
        <Styles.StatusContainer>
          <CircularProgress size={24} />
          <Typography sx={{ ml: 2 }} variant="body2">
            Transcription in progress... This may take several minutes.
          </Typography>
        </Styles.StatusContainer>
      </Styles.Container>
    );
  }

  if (transcription.transcription_status === 'failed') {
    return (
      <Styles.Container>
        <Styles.ErrorContainer>
          <Typography color="error" variant="body1">
            Transcription failed
          </Typography>
          {transcription.error_message && (
            <Typography sx={{ mt: 1 }} variant="body2" color="textSecondary">
              {transcription.error_message}
            </Typography>
          )}
        </Styles.ErrorContainer>
      </Styles.Container>
    );
  }

  return (
    <Styles.Container>
      <Styles.Header>
        <Styles.MetadataRow>
          <Styles.MetadataItem>
            <Styles.MetadataLabel>Duration:</Styles.MetadataLabel>
            <Styles.MetadataValue>
              {formatTimestamp(transcription.duration_seconds || 0)}
            </Styles.MetadataValue>
          </Styles.MetadataItem>
          <Styles.MetadataItem>
            <Styles.MetadataLabel>Words:</Styles.MetadataLabel>
            <Styles.MetadataValue>
              {transcription.word_count || 0}
            </Styles.MetadataValue>
          </Styles.MetadataItem>
          <Styles.MetadataItem>
            <Styles.MetadataLabel>Speakers:</Styles.MetadataLabel>
            <Styles.MetadataValue>
              {transcription.speaker_count || 0}
            </Styles.MetadataValue>
          </Styles.MetadataItem>
          <Styles.MetadataItem>
            <Styles.MetadataLabel>Confidence:</Styles.MetadataLabel>
            <Styles.MetadataValue>
              {transcription.confidence_avg
                ? `${Math.round(transcription.confidence_avg * 100)}%`
                : 'N/A'}
            </Styles.MetadataValue>
          </Styles.MetadataItem>
        </Styles.MetadataRow>

        <TextField
          fullWidth
          size="small"
          sx={{ mt: 2 }}
          value={searchQuery}
          placeholder="Search transcript..."
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Styles.Header>

      <Styles.TranscriptContent>
        {filteredUtterances.length === 0 ? (
          <Styles.EmptyState>
            <Typography variant="body2" color="textSecondary">
              No results found for "{searchQuery}"
            </Typography>
          </Styles.EmptyState>
        ) : (
          filteredUtterances.map((utterance, index) => (
            <Styles.UtteranceContainer
              key={index}
              isActive={index === activeUtteranceIndex}
              ref={(el: HTMLDivElement | null) =>
                (utteranceRefs.current[index] = el)
              }
            >
              <Styles.SpeakerInfo>
                <Styles.SpeakerLabel color={getSpeakerColor(utterance.speaker)}>
                  {getSpeakerLabel(utterance.speaker)}
                </Styles.SpeakerLabel>
                <Styles.SpeakerTimestamp>
                  {formatTimestamp(utterance.startTime)}
                </Styles.SpeakerTimestamp>
              </Styles.SpeakerInfo>

              <Styles.UtteranceContent>
                <Styles.UtteranceText>
                  {highlightSearchText(
                    utterance.text,
                    searchQuery || highlightText
                  )}
                </Styles.UtteranceText>

                <Styles.UtteranceFooter>
                  <Styles.TimestampButton
                    disabled={!onTimestampClick}
                    onClick={() => onTimestampClick?.(utterance.startTime)}
                  >
                    {formatTimestamp(utterance.startTime)}
                  </Styles.TimestampButton>

                  <Styles.ConfidenceIndicator>
                    {Math.round(utterance.confidence * 100)}% confident
                  </Styles.ConfidenceIndicator>
                </Styles.UtteranceFooter>
              </Styles.UtteranceContent>
            </Styles.UtteranceContainer>
          ))
        )}
      </Styles.TranscriptContent>
    </Styles.Container>
  );
};
